// components/Clinician/RISWorklist.jsx
// RIS Worklist: "What needs to happen with this examination?"
// Pure RIS domain — fetches from /orders, shows all exam lifecycle statuses.
// NO Orthanc, NO PACS image data, NO DycoVue workflow columns.
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { studyHasImages } from './workflowStatus';
import {
  Box, Paper, Typography, TextField, Button, Table, Chip,
  TableBody, TableCell, TableContainer, TableHead, TableRow,
  Select, MenuItem, FormControl, InputAdornment, Tabs, Tab,
  CircularProgress, IconButton, Tooltip, Snackbar, Alert, Badge,
  Menu, Divider, ListItemIcon, ListItemText, TablePagination
} from '@mui/material';
import {
  Search, FilterList, Refresh, AccessTime, FlagOutlined,
  PersonOutline, MedicalServicesOutlined, MoreVert,
  HowToReg, PlayCircleOutline, CheckCircleOutline, CancelOutlined
} from '@mui/icons-material';
import { orderService } from '../../services/ris';
import { isRisOrderCompleted } from '../../utils/risReportUtils';
import { useThemeMode } from '../../contexts/ThemeModeContext';

// ─── RIS Order Status Definitions ────────────────────────────────────────────
const ORDER_STATUSES = {
  NEW:       { label: 'Scheduled',      color: '#6B7280', bg: '#F3F4F6', darkColor: '#9CA3AF', darkBg: 'rgba(255,255,255,0.08)', tab: 0 },
  READY:     { label: 'Ready',          color: '#3B82F6', bg: '#EFF6FF', darkColor: '#60A5FA', darkBg: 'rgba(59,130,246,0.18)', tab: 1 },
  ARRIVAL:   { label: 'Arrived',        color: '#8B5CF6', bg: '#F5F3FF', darkColor: '#A78BFA', darkBg: 'rgba(139,92,246,0.18)', tab: 1 },
  ARRIVED:   { label: 'Arrived',        color: '#8B5CF6', bg: '#F5F3FF', darkColor: '#A78BFA', darkBg: 'rgba(139,92,246,0.18)', tab: 1 },
  STARTED:   { label: 'In Exam Room',   color: '#2563EB', bg: '#DBEAFE', darkColor: '#60A5FA', darkBg: 'rgba(37,99,235,0.18)', tab: 2 },
  ENDEXAM:   { label: 'Exam Complete',  color: '#D97706', bg: '#FEF3C7', darkColor: '#FBBF24', darkBg: 'rgba(217,119,6,0.18)', tab: 3 },
  TOREPORT:  { label: 'To Report',      color: '#EA580C', bg: '#FFF7ED', darkColor: '#FB923C', darkBg: 'rgba(234,88,12,0.18)', tab: 3 },
  PRELIM:    { label: 'Preliminary',    color: '#EA580C', bg: '#FFF7ED', darkColor: '#FB923C', darkBg: 'rgba(234,88,12,0.18)', tab: 3 },
  QC:        { label: 'QC Review',      color: '#7C3AED', bg: '#EDE9FE', darkColor: '#A78BFA', darkBg: 'rgba(124,58,237,0.18)', tab: 3 },
  REPORTED:  { label: 'Reported',       color: '#10B981', bg: '#D1FAE5', darkColor: '#34D399', darkBg: 'rgba(16,185,129,0.18)', tab: 4 },
  CANCELLED: { label: 'Cancelled',      color: '#EF4444', bg: '#FEF2F2', darkColor: '#F87171', darkBg: 'rgba(239,68,68,0.18)', tab: 5 },
};

const PRIORITY_STYLES = {
  STAT:    { label: 'STAT',    color: '#EF4444', bg: '#FEF2F2', darkColor: '#F87171', darkBg: 'rgba(239,68,68,0.18)' },
  URGENT:  { label: 'URGENT', color: '#F59E0B', bg: '#FFFBEB', darkColor: '#FBBF24', darkBg: 'rgba(245,158,11,0.18)' },
  ROUTINE: { label: 'Routine', color: '#6B7280', bg: '#F3F4F6', darkColor: '#9CA3AF', darkBg: 'rgba(255,255,255,0.08)' },
};

const TABS = [
  { label: 'All',            key: 'all' },
  { label: 'Scheduled',      key: 'scheduled' },
  { label: 'In Exam Room',   key: 'exam_room' },
  { label: 'Awaiting Report',key: 'awaiting' },
  { label: 'Reported',       key: 'reported' },
  { label: 'Cancelled',      key: 'cancelled' },
];

function tatLabel(orderedAt) {
  if (!orderedAt) return '—';
  const mins = Math.floor((Date.now() - new Date(orderedAt).getTime()) / 60000);
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  return `${Math.floor(hrs / 24)}d`;
}

function tatColor(orderedAt, orderStatus) {
  if (!orderedAt || orderStatus === 'REPORTED' || orderStatus === 'CANCELLED') return 'default';
  const hrs = (Date.now() - new Date(orderedAt).getTime()) / 3600000;
  if (hrs > 24) return 'error';
  if (hrs > 8) return 'warning';
  return 'default';
}

function StatusChip({ orderStatus, isDark }) {
  const s = ORDER_STATUSES[orderStatus] || ORDER_STATUSES.NEW;
  return (
    <Chip
      label={s.label}
      size="small"
      sx={{
        color: isDark ? (s.darkColor || s.color) : s.color,
        bgcolor: isDark ? (s.darkBg || 'rgba(255,255,255,0.08)') : s.bg,
        fontWeight: 700,
        fontSize: '0.62rem',
        height: 16,
        '& .MuiChip-label': { px: 0.6 },
      }}
    />
  );
}

function PriorityChip({ priority, isDark }) {
  const p = PRIORITY_STYLES[priority?.toUpperCase()] || PRIORITY_STYLES.ROUTINE;
  return (
    <Chip
      label={p.label}
      size="small"
      sx={{
        color: isDark ? (p.darkColor || p.color) : p.color,
        bgcolor: isDark ? (p.darkBg || 'rgba(255,255,255,0.08)') : p.bg,
        fontWeight: 700,
        fontSize: '0.62rem',
        height: 16,
        '& .MuiChip-label': { px: 0.6 },
      }}
    />
  );
}

// ─── Component ────────────────────────────────────────────────────────────────
export default function RISWorklist() {
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';
  const cardBg = isDark ? '#1E1E1E' : '#FFFFFF';
  const inputBg = isDark ? '#121212' : '#FFFFFF';
  const cardBorderColor = isDark ? 'rgba(255, 255, 255, 0.08)' : '#E2E8F0';
  const cardBorder = `1px solid ${cardBorderColor}`;
  const textColorPrimary = isDark ? '#F1F5F9' : '#0F172A';
  const textColorSecondary = isDark ? '#CBD5E1' : '#64748B';
  const tableHeaderBg = isDark ? '#18181B' : '#F8FAFC';

  const [orders, setOrders]     = useState([]);
  const [total, setTotal]       = useState(0);
  const [loading, setLoading]   = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [page, setPage]         = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [search, setSearch]     = useState('');
  const [modality, setModality] = useState('All');
  const [priority, setPriority] = useState('All');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuOrder, setMenuOrder] = useState(null);
  const refreshTimerRef = useRef(null);

  const openMenu  = (e, order) => { e.stopPropagation(); setMenuAnchor(e.currentTarget); setMenuOrder(order); };
  const closeMenu = () => { setMenuAnchor(null); setMenuOrder(null); };

  const handleStatusChange = async (newStatus) => {
    closeMenu();
    try {
      await orderService.updateStatus(menuOrder._id, newStatus);
      setSnackbar({ open: true, message: `Status updated to ${ORDER_STATUSES[newStatus]?.label || newStatus}`, severity: 'success' });
      fetchOrders(true);
    } catch {
      setSnackbar({ open: true, message: 'Failed to update status', severity: 'error' });
    }
  };

  const handleCancel = async () => {
    closeMenu();
    try {
      await orderService.cancel(menuOrder._id, 'Cancelled from RIS Worklist');
      setSnackbar({ open: true, message: 'Order cancelled', severity: 'info' });
      fetchOrders(true);
    } catch {
      setSnackbar({ open: true, message: 'Failed to cancel order', severity: 'error' });
    }
  };

  const MODALITIES = ['CT', 'MRI', 'XRAY', 'US', 'MAMMO', 'ANGIO', 'BMD'];

  const fetchOrders = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const params = { limit: 500 };

      if (search) {
        if (/^\d+$/.test(search)) params.mrn = search;
        else if (search.toLowerCase().includes('ord-') || search.toLowerCase().includes('dv-')) params.accession = search;
        else params.patientName = search;
      }
      if (modality !== 'All') params.modality = modality;
      if (priority !== 'All') params.priority = priority.toUpperCase();

      // Always load the full set (including cancelled) in one request so the
      // per-status tab counts stay stable regardless of which tab is selected.
      params.includeCancelled = 'true';

      const { data } = await orderService.list(params);
      const list = (data.orders || []).sort((a, b) => {
        const da = new Date(a.createdAt || a.orderedAt || 0).getTime();
        const db = new Date(b.createdAt || b.orderedAt || 0).getTime();
        return db - da;
      });
      setOrders(list);
      setTotal(data.total || list.length);
    } catch (err) {
      if (!silent) setSnackbar({ open: true, message: 'Failed to load worklist', severity: 'error' });
    } finally {
      if (!silent) setLoading(false);
    }
  }, [search, modality, priority]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // Silent refresh every 2 minutes
  useEffect(() => {
    refreshTimerRef.current = setInterval(() => fetchOrders(true), 120000);
    return () => clearInterval(refreshTimerRef.current);
  }, [fetchOrders]);

  // ── Tab Filtering ──────────────────────────────────────────────────────────
  const filtered = orders.filter((o) => {
    const code = (o.orderStatus || 'NEW').toUpperCase();
    switch (activeTab) {
      case 1: return ['NEW', 'READY', 'ARRIVAL', 'ARRIVED'].includes(code);
      case 2: return code === 'STARTED';
      case 3: return ['ENDEXAM', 'TOREPORT', 'PRELIM', 'QC'].includes(code) && studyHasImages(o);
      case 4: return code !== 'CANCELLED' && (code === 'REPORTED' || isRisOrderCompleted(o));
      case 5: return code === 'CANCELLED';
      default: return code !== 'CANCELLED';
    }
  });

  // ── Tab counts ─────────────────────────────────────────────────────────────
  // Computed from the full (loaded once) dataset so they never change when a tab is picked.
  const activeOrders = orders.filter(o => (o.orderStatus || '').toUpperCase() !== 'CANCELLED');
  const counts = {
    0: activeOrders.length,
    1: activeOrders.filter(o => ['NEW','READY','ARRIVAL','ARRIVED'].includes((o.orderStatus||'').toUpperCase())).length,
    2: activeOrders.filter(o => (o.orderStatus||'').toUpperCase() === 'STARTED').length,
    3: activeOrders.filter(o => ['ENDEXAM','TOREPORT','PRELIM','QC'].includes((o.orderStatus||'').toUpperCase()) && studyHasImages(o)).length,
    4: activeOrders.filter(o => (o.orderStatus||'').toUpperCase() === 'REPORTED' || isRisOrderCompleted(o)).length,
    5: orders.filter(o => (o.orderStatus||'').toUpperCase() === 'CANCELLED').length,
  };

  const formatName = (raw) => (raw || '').replace(/\^/g, ' ').trim() || '—';
  const fmtDate = (v) => v ? new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
  const fmtAge = (birthDate) => {
    if (!birthDate) return '';
    try {
      const birth = new Date(birthDate);
      if (isNaN(birth.getTime())) return '';
      const diffMs = Date.now() - birth.getTime();
      const ageDate = new Date(diffMs);
      const years = Math.abs(ageDate.getUTCFullYear() - 1970);
      return `${years} yrs`;
    } catch {
      return '';
    }
  };

  return (
    <Box sx={{ maxWidth: '1600px', mx: 'auto', p: { xs: 0.75, sm: 1 } }}>
      {/* ── Header ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 800, fontSize: '1rem', lineHeight: 1.2, color: 'text.primary' }}>
            RIS Worklist
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
            Examination scheduling and workflow — {counts[0]} orders
          </Typography>
        </Box>
        <Tooltip title="Refresh">
          <IconButton size="small" onClick={() => fetchOrders()} disabled={loading} sx={{ width: 26, height: 26 }}>
            {loading ? <CircularProgress size={14} /> : <Refresh sx={{ fontSize: 16 }} />}
          </IconButton>
        </Tooltip>
      </Box>

      {/* ── Filters ── */}
      <Paper sx={{ p: '4px 10px', mb: 0.75, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', borderRadius: '6px', border: cardBorder, bgcolor: cardBg }}>
        <TextField
          size="small"
          placeholder="MRN, patient name, accession…"
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(0); }}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 14 }} /></InputAdornment> }}
          sx={{ minWidth: 200, '& .MuiOutlinedInput-root': { height: 26, fontSize: '0.76rem', bgcolor: inputBg } }}
        />
        <FormControl size="small" sx={{ minWidth: 110 }}>
          <Select value={modality} onChange={e => { setModality(e.target.value); setPage(0); }} displayEmpty sx={{ height: 26, fontSize: '0.76rem', bgcolor: inputBg, '& .MuiSelect-select': { py: '2px', px: '6px' } }}>
            <MenuItem value="All" sx={{ fontSize: '0.76rem' }}>All Modalities</MenuItem>
            {MODALITIES.map(m => <MenuItem key={m} value={m} sx={{ fontSize: '0.76rem' }}>{m}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 110 }}>
          <Select value={priority} onChange={e => { setPriority(e.target.value); setPage(0); }} displayEmpty sx={{ height: 26, fontSize: '0.76rem', bgcolor: inputBg, '& .MuiSelect-select': { py: '2px', px: '6px' } }}>
            <MenuItem value="All" sx={{ fontSize: '0.76rem' }}>All Priorities</MenuItem>
            <MenuItem value="STAT" sx={{ fontSize: '0.76rem' }}>STAT</MenuItem>
            <MenuItem value="ROUTINE" sx={{ fontSize: '0.76rem' }}>Routine</MenuItem>
          </Select>
        </FormControl>
        {(modality !== 'All' || priority !== 'All' || search) && (
          <Button size="small" onClick={() => { setSearch(''); setModality('All'); setPriority('All'); setPage(0); }} sx={{ height: 26, fontSize: '0.72rem', textTransform: 'none', py: 0, px: 1 }}>
            Clear
          </Button>
        )}
        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <FilterList sx={{ fontSize: 14, color: 'text.secondary' }} />
          <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>{filtered.length} shown</Typography>
        </Box>
      </Paper>

      {/* ── Tabs ── */}
      <Tabs
        value={activeTab}
        onChange={(_, v) => { setActiveTab(v); setPage(0); }}
        sx={{ mb: 0.5, borderBottom: 1, borderColor: cardBorderColor, minHeight: 28 }}
        variant="scrollable"
        scrollButtons="auto"
      >
        {TABS.map((t, i) => (
          <Tab
            key={t.key}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <span>{t.label}</span>
                {counts[i] > 0 && (
                  <Box
                    component="span"
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minWidth: 18,
                      height: 16,
                      px: '5px',
                      borderRadius: '8px',
                      fontSize: '0.62rem',
                      fontWeight: 700,
                      lineHeight: 1,
                      color: i === 3 ? '#B45309' : i === 2 ? '#1D4ED8' : (isDark ? '#CBD5E1' : '#475569'),
                      bgcolor: i === 3
                        ? (isDark ? 'rgba(245,158,11,0.2)' : '#FEF3C7')
                        : i === 2
                          ? (isDark ? 'rgba(37,99,235,0.2)' : '#DBEAFE')
                          : (isDark ? 'rgba(255,255,255,0.1)' : '#F1F5F9'),
                    }}
                  >
                    {counts[i]}
                  </Box>
                )}
              </Box>
            }
            sx={{ textTransform: 'none', minWidth: 'auto', px: 1.5, py: 0.25, minHeight: 28, fontSize: '0.75rem' }}
          />
        ))}
      </Tabs>

      {/* ── Table ── */}
      <TableContainer component={Paper} sx={{ borderRadius: '6px', overflow: 'hidden', border: cardBorder, bgcolor: cardBg }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: tableHeaderBg }}>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.68rem', color: textColorSecondary, textTransform: 'uppercase', py: '3px !important', px: '6px !important' }}>MRN</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.68rem', color: textColorSecondary, textTransform: 'uppercase', py: '3px !important', px: '6px !important' }}>Accession</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.68rem', color: textColorSecondary, textTransform: 'uppercase', py: '3px !important', px: '6px !important' }}>Patient</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.68rem', color: textColorSecondary, textTransform: 'uppercase', py: '3px !important', px: '6px !important' }}>Procedure</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.68rem', color: textColorSecondary, textTransform: 'uppercase', py: '3px !important', px: '6px !important' }}>Ref. Physician</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.68rem', color: textColorSecondary, textTransform: 'uppercase', py: '3px !important', px: '6px !important' }}>Priority</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.68rem', color: textColorSecondary, textTransform: 'uppercase', py: '3px !important', px: '6px !important' }}>Exam Status</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.68rem', color: textColorSecondary, textTransform: 'uppercase', py: '3px !important', px: '6px !important' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                  <AccessTime sx={{ fontSize: 11 }} /> TAT
                </Box>
              </TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.68rem', color: textColorSecondary, textTransform: 'uppercase', py: '3px !important', px: '6px !important' }}>Ordered</TableCell>
              <TableCell sx={{ py: '3px !important', px: '4px !important', width: 32 }} />
            </TableRow>
          </TableHead>

          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ py: 3 }}>
                  <CircularProgress size={20} />
                </TableCell>
              </TableRow>
            )}

            {!loading && filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ py: 3, color: textColorSecondary, fontSize: '0.78rem' }}>
                  No orders found
                </TableCell>
              </TableRow>
            )}

            {!loading && filtered.slice(page * rowsPerPage, (page + 1) * rowsPerPage).map((order) => {
              const tatSeverity = tatColor(order.orderedAt || order.createdAt, order.orderStatus);
              const tat = tatLabel(order.orderedAt || order.createdAt);
              const mrnDisplay = order.patientMrn || order.patient?.mrn || order.mrn || order.patientId || '—';
              const ageDisplay = fmtAge(order.patientBirthDate || order.patient?.birthDate) || order.patientAge || '—';

              return (
                <TableRow
                  key={order._id}
                  hover
                  sx={{
                    height: '28px',
                    borderLeft: order.priority === 'STAT' ? '3px solid #EF4444' : '3px solid transparent',
                  }}
                >
                  <TableCell sx={{ py: '2px !important', px: '6px !important' }}>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600, fontSize: '0.72rem', color: textColorPrimary, lineHeight: 1.1 }}>
                      {mrnDisplay}
                    </Typography>
                  </TableCell>

                  <TableCell sx={{ py: '2px !important', px: '6px !important' }}>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.72rem', color: textColorSecondary, lineHeight: 1.1 }}>
                      {order.accessionNumber || '—'}
                    </Typography>
                  </TableCell>

                  <TableCell sx={{ py: '2px !important', px: '6px !important' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <PersonOutline sx={{ fontSize: 13, color: 'text.disabled', flexShrink: 0 }} />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" noWrap sx={{ fontWeight: 600, fontSize: '0.72rem', lineHeight: 1.15, color: textColorPrimary }}>
                          {formatName(order.patientName)}
                        </Typography>
                        <Typography variant="caption" sx={{ fontSize: '0.62rem', lineHeight: 1, color: textColorSecondary, display: 'block', mt: '1px' }}>
                          {order.patientSex || order.patient?.sex || '?'} · {ageDisplay}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>

                  <TableCell sx={{ py: '2px !important', px: '6px !important' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                      <MedicalServicesOutlined sx={{ fontSize: 12, color: 'text.disabled', flexShrink: 0 }} />
                      <Typography variant="body2" noWrap sx={{ maxWidth: 180, fontSize: '0.72rem', lineHeight: 1.15, color: textColorPrimary }}>
                        {order.procedureDescription || order.procedureCode || '—'}
                      </Typography>
                    </Box>
                    {order.modality && (
                      <Typography variant="caption" sx={{ fontSize: '0.62rem', lineHeight: 1, color: textColorSecondary, display: 'block', mt: '1px', ml: '16px' }}>
                        {order.modality}
                      </Typography>
                    )}
                  </TableCell>

                  <TableCell sx={{ py: '2px !important', px: '6px !important' }}>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 120, fontSize: '0.72rem', color: textColorPrimary, lineHeight: 1.1 }}>
                      {order.referrerName || '—'}
                    </Typography>
                  </TableCell>

                  <TableCell sx={{ py: '2px !important', px: '6px !important' }}>
                    <PriorityChip priority={order.priority || 'ROUTINE'} isDark={isDark} />
                  </TableCell>

                  <TableCell sx={{ py: '2px !important', px: '6px !important' }}>
                    <StatusChip orderStatus={order.orderStatus || 'NEW'} isDark={isDark} />
                  </TableCell>

                  <TableCell sx={{ py: '2px !important', px: '6px !important' }}>
                    <Chip
                      icon={<AccessTime sx={{ fontSize: '10px !important' }} />}
                      label={tat}
                      size="small"
                      color={tatSeverity}
                      variant={tatSeverity === 'default' ? 'outlined' : 'filled'}
                      sx={{
                        fontFamily: 'monospace',
                        fontSize: '0.62rem',
                        height: 16,
                        '& .MuiChip-label': { px: 0.5 },
                        '& .MuiChip-icon': { ml: '3px', mr: '-2px' },
                      }}
                    />
                  </TableCell>

                  <TableCell sx={{ py: '2px !important', px: '6px !important' }}>
                    <Typography variant="caption" sx={{ fontSize: '0.68rem', color: 'text.secondary', lineHeight: 1.1, whiteSpace: 'nowrap' }}>
                      {fmtDate(order.orderedAt || order.createdAt)}
                    </Typography>
                  </TableCell>

                  <TableCell align="right" sx={{ py: '2px !important', px: '2px !important' }}>
                    <Tooltip title="More actions">
                      <IconButton size="small" onClick={(e) => openMenu(e, order)} sx={{ width: 20, height: 20, p: 0 }}>
                        <MoreVert sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={filtered.length}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[15, 25, 50, 100]}
          sx={{
            borderTop: 1,
            borderColor: 'divider',
            '.MuiTablePagination-toolbar': { minHeight: 28, px: 1 },
            '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': { fontSize: '0.68rem' },
            '.MuiTablePagination-select': { fontSize: '0.68rem' }
          }}
        />
      </TableContainer>

      {/* ── Row context menu ── */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
        PaperProps={{ sx: { minWidth: 200, borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.12)' } }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {/* Status-advancing actions — only show steps that apply to the current status */}
        {['NEW','READY','ARRIVAL','ARRIVED'].includes((menuOrder?.orderStatus||'').toUpperCase()) && (
          <MenuItem onClick={() => handleStatusChange('ARRIVED')}>
            <ListItemIcon><HowToReg fontSize="small" sx={{ color: '#8B5CF6' }} /></ListItemIcon>
            <ListItemText primary="Mark as Arrived" />
          </MenuItem>
        )}
        {(menuOrder?.orderStatus||'').toUpperCase() === 'ARRIVED' && (
          <MenuItem onClick={() => handleStatusChange('STARTED')}>
            <ListItemIcon><PlayCircleOutline fontSize="small" sx={{ color: '#2563EB' }} /></ListItemIcon>
            <ListItemText primary="Start Exam" />
          </MenuItem>
        )}
        {(menuOrder?.orderStatus||'').toUpperCase() === 'STARTED' && (
          <MenuItem onClick={() => handleStatusChange('ENDEXAM')}>
            <ListItemIcon><CheckCircleOutline fontSize="small" sx={{ color: '#D97706' }} /></ListItemIcon>
            <ListItemText primary="Mark Exam Complete" />
          </MenuItem>
        )}
        {['ENDEXAM','TOREPORT','QC'].includes((menuOrder?.orderStatus||'').toUpperCase()) && (
          <MenuItem onClick={() => handleStatusChange('REPORTED')}>
            <ListItemIcon><CheckCircleOutline fontSize="small" sx={{ color: '#10B981' }} /></ListItemIcon>
            <ListItemText primary="Mark as Reported" />
          </MenuItem>
        )}

        {/* Cancel — only for non-terminal statuses */}
        {!['CANCELLED','REPORTED'].includes((menuOrder?.orderStatus||'').toUpperCase()) && [
          <Divider key="div" />,
          <MenuItem key="cancel" onClick={handleCancel} sx={{ color: 'error.main' }}>
            <ListItemIcon><CancelOutlined fontSize="small" color="error" /></ListItemIcon>
            <ListItemText primary="Cancel Order" />
          </MenuItem>
        ]}
      </Menu>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
